/**
 * How much was logged each day, lately (T-903).
 *
 * anytracker exists to let people track anything **and to encourage them to
 * keep doing it**. A record nobody keeps up is worth nothing, so making
 * consistency visible is part of the product rather than a bolt-on.
 *
 * The design constraint worth writing down: encouragement is the goal,
 * distorting the record is not. A streak that pressures someone into logging
 * *something* on a day they would otherwise skip has manufactured data, and
 * this app's whole value is that its history is a record of what happened.
 *
 * Two things keep those apart. The strip reports **how much** was logged rather
 * than only whether — a light day looks different from a heavy one, so the
 * honest answer is not flattened into a binary. And the count of consecutive
 * days is `adherenceFor`'s `trailingStreakDays`, which is offered on the way
 * past and never used as leverage: nothing here knows how to say a streak is at
 * risk.
 */

import type { Entry } from '../model/entry.js'
import { dayKey, shiftDayKey } from '../model/timestamp.js'
import type { Timestamp } from '../model/timestamp.js'

/**
 * Which band a day falls into, matching the `--heat-*` scale.
 *
 * Here rather than at each call site so Home and the post-save confirmation
 * cannot disagree about what a day's colour means. Four bands because that is
 * how many the palette has; the thresholds are the interesting part.
 */
export type ConsistencyBand = 0 | 1 | 2 | 3

export interface ConsistencyDay {
  readonly dayKey: string
  readonly count: number
  readonly band: ConsistencyBand
}

export interface Consistency {
  readonly days: readonly ConsistencyDay[]
  /** Days in the window carrying at least one entry. */
  readonly loggedDays: number
  readonly totalDays: number
}

export function bandFor(count: number): ConsistencyBand {
  if (count <= 0) {
    return 0
  }
  if (count === 1) {
    return 1
  }
  if (count === 2) {
    return 2
  }
  return 3
}

/**
 * Per-day counts over a trailing window ending today.
 *
 * Days are counted in each entry's **captured** zone — `dayKey` reads the
 * timestamp's own `timeZone`, so an entry logged at 23:30 before flying counts
 * on the day it was made rather than being dragged into the reader's calendar
 * (ADR-0006).
 *
 * The window's own days come from the viewer's zone, because "the last
 * fourteen days" is a question about now and now is where the reader is. Today
 * is read in that zone once, and the rest of the window is walked back from it
 * as **calendar** days — stepping instants back by 86,400,000ms would drop a
 * day from any fortnight containing a spring-forward.
 */
export function consistency(
  entries: readonly Entry[],
  days: number,
  now: Timestamp,
  timeZone: string,
): Consistency {
  const counts = new Map<string, number>()
  for (const entry of entries) {
    const key = dayKey(entry.at)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }

  const today = dayKey({ epochMs: now.epochMs, timeZone })
  const window: ConsistencyDay[] = []
  for (let offset = days - 1; offset >= 0; offset -= 1) {
    const key = shiftDayKey(today, -offset)
    const count = counts.get(key) ?? 0
    window.push({ dayKey: key, count, band: bandFor(count) })
  }

  return {
    days: window,
    loggedDays: window.filter((day) => day.count > 0).length,
    totalDays: window.length,
  }
}

/**
 * The caption beside the strip.
 *
 * In core with the numbers, because a strip is a shape and this is the fact.
 * A row of cells that is the only place a number appears is not a number
 * anybody can read, and it is invisible to a screen reader entirely.
 */
export function describeConsistency(summary: Consistency): string {
  const { loggedDays, totalDays } = summary
  const days = loggedDays === 1 ? '1 day' : `${String(loggedDays)} days`
  return `${days} logged in the last ${String(totalDays)}`
}

/** "6 days in a row", or `null` when there is nothing to celebrate yet. */
export function describeStreak(trailingStreakDays: number): string | null {
  if (trailingStreakDays < 1) {
    return null
  }
  return trailingStreakDays === 1
    ? '1 day in a row'
    : `${String(trailingStreakDays)} days in a row`
}
